import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SocialLogin from "../../auth2/SocialLogin/SocialLogin";
import qushiLogo from "../../../images/logo-green.png";
import LegalAgreementCheckbox from "../../../components/auth/LegalAgreementCheckbox";
import useAuthScreenBackground from "../../../hooks/useAuthScreenBackground";
import {
  LEGAL_ACCEPTANCE_ERROR,
  clearLegalAccepted,
  setLegalAccepted,
} from "../../../utils/legalAcceptance";

const isMobile = window.innerWidth < 768;

const PrimaryButton = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="min-w-full sm:min-w-80 bg-primaryColor text-white rounded-2xl sm:p-6 py-4 px-6 font-bold text-xl shadow-lg border-2 border-green-600 focus:outline-none active:opacity-80 transition"
  >
    {children}
  </button>
);

const TextLink = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-primaryColor text-base focus:outline-none font-semibold"
  >
    {children}
  </button>
);

const BodyText = ({ children, className = "" }) => (
  <p
    className={`text-primaryColor sm:text-3xl text-2xl leading-snug text-center font-montserrat ${className}`}
    style={{ fontWeight: 300 }}
  >
    {children}
  </p>
);

const QushiLogo = () => (
  <img src={qushiLogo} alt="QUSHi" style={{ width: "120px" }} />
);

/**
 * `initialStep` lets a route open one step of this flow directly. /auth/create-account
 * uses it to land on the "Create your free QUSHi account" screen (step 6), which is
 * where every "Create Account" link now points - the email form is only reached from
 * "Sign up with email" on that screen, so the legal checkbox is never skipped.
 */
function MobileOnboarding({ initialStep = 0 }) {
  // Cream all the way to the screen edges - see the hook for why the wrapper
  // div alone could not reach behind the status bar and home indicator.
  useAuthScreenBackground();

  const navigate = useNavigate();
  const [step, setStep] = useState(initialStep);
  const [navLock, setNavLock] = useState(false);

  // The legal + age checkbox on the account-creation step. Unchecked on every
  // mount - an affirmative act each time, never restored from a previous visit.
  const [legalAccepted, setLegalAcceptedState] = useState(false);
  const [legalError, setLegalError] = useState(null);

  const handleLegalChange = (next) => {
    setLegalAcceptedState(next);
    if (next) {
      setLegalError(null);
      // Persisted so whichever request finally creates the account - signup or
      // social-login, possibly on the next screen - can send it along.
      setLegalAccepted();
    } else {
      clearLegalAccepted();
    }
  };

  const blockUntilAccepted = () => setLegalError(LEGAL_ACCEPTANCE_ERROR);

  const goToEmailSignup = () => {
    if (!legalAccepted) {
      blockUntilAccepted();
      return;
    }
    navigate("/auth/register");
  };

  const markSeen = (persona) => {
    localStorage.setItem("qushi_onboarding_seen", "true");
    if (persona) {
      localStorage.setItem("qushi_onboarding_persona", persona);
      localStorage.setItem("qushi_newtrader_guide_pending", "true");
      localStorage.removeItem("qushi_newtrader_guide_done");
    }
  };

  const advanceTo = (newStep) => {
    if (navLock) return;
    setNavLock(true);
    setStep(newStep);
    setTimeout(() => setNavLock(false), 400);
  };

  const next = () => advanceTo(step + 1);
  const goToChooser = (persona) => {
    if (!persona) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "tutorial_skip" });
    }
    markSeen(persona);
    advanceTo(6);
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div className="flex flex-col flex-1 items-center text-center justify-center">
            <h1 className="text-primaryColor font-bold text-[36px] sm:text-5xl leading-tight mb-8 text-center font-montserrat">
              Learn trading
              <br />
              by doing it
            </h1>
            <BodyText>
              Real market data.
              <br />
              Virtual money.
              <br />
              Zero pressure.
            </BodyText>
            <div className="mt-8">
              <PrimaryButton
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({ event: "tutorial_begin" });
                  next();
                }}
              >
                Start quick intro
              </PrimaryButton>
            </div>
            {/* "Watch instead" removed at the client's request; "Skip and start"
                takes its position rather than sitting under a gap. */}
            <div className="mt-8 flex flex-col items-center gap-5 font-montserrat">
              <TextLink onClick={() => goToChooser()}>Skip and start</TextLink>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="flex flex-col flex-1 items-center text-center justify-center">
            <h1 className="text-primaryColor font-bold text-[36px] sm:text-5xl mb-8 font-montserrat">
              You're early
            </h1>
            <BodyText>
              QUSHi is free at launch. We want early users helping shape what
              this becomes.
            </BodyText>
            <div className="mt-12">
              <PrimaryButton onClick={next}>I'm ready</PrimaryButton>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="flex flex-col flex-1 items-center text-center justify-center">
            <h1 className="text-primaryColor font-montserrat font-bold text-[36px] sm:text-5xl leading-tight mb-8">
              Start with <span className="inline-block">$1 million</span>
              <br />
              in virtual money
            </h1>
            <BodyText>
              Trade US stocks, crypto, and forex using real-time market data
              without risking a single dollar.
            </BodyText>
            <div className="mt-12">
              <PrimaryButton onClick={next}>I'm in</PrimaryButton>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="flex flex-col flex-1 items-center text-center justify-center">
            <h1 className="text-primaryColor font-bold text-[36px] sm:text-5xl mb-8 font-montserrat">
              No fake guru energy
            </h1>
            <BodyText className="mb-6">
              This is a safe place to learn. Every trade is virtual, but every
              track record is real.
            </BodyText>
            <BodyText className="mb-6">
              Follow traders who can back it up.
            </BodyText>
            <p className="text-greenXD text-2xl font-bold font-montserrat">
              No hype. Just proof.
            </p>
            <div className="mt-12">
              <PrimaryButton onClick={next}>That makes sense</PrimaryButton>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="flex flex-col flex-1 items-center text-center justify-center">
            <h1 className="text-primaryColor font-bold text-[36px] sm:text-5xl mb-8 font-montserrat">
              More than a simulator
            </h1>
            <BodyText className="mb-6">
              The more you trade, the more you unlock.
            </BodyText>
            <BodyText className="mb-6">
              Advanced charts, leagues, and{" "}
              <span className="font-bold font-montserrat">Cash Games</span>: free-to-enter,
              skill-based contests with real prizes.
            </BodyText>
            <p className="text-greenXD text-2xl font-bold font-montserrat">
              All upside. No risk.
            </p>
            <div className="mt-12">
              <PrimaryButton
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({ event: "tutorial_complete" });
                  next();
                }}
              >
                Keep going
              </PrimaryButton>
            </div>
          </div>
        );

      case 5: {
        const personas = [
          { id: "new", label: "I'm brand new\nto trading" },
          { id: "basics", label: "I know the basics and\nwant to practice" },
          { id: "experienced", label: "I'm experienced and\nwant to compete" },
          { id: "creator", label: "I'm here to build a\nfollowing and monetize" },
        ];
        return (
          <div className="flex flex-col flex-1 items-center text-center justify-center">
            <h1 className="text-primaryColor font-bold font-montserrat text-[36px] sm:text-5xl leading-tight text-center mb-3">
              What describes
              <br />
              you best
            </h1>
            <p className="text-primaryColor text-xl text-center mb-8 font-montserrat">
              We'll personalize your first step
            </p>
            <div className="flex flex-col gap-4">
              {personas.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => goToChooser(p.id)}
                  className="w-full border-2 border-primaryColor text-primaryColor rounded-2xl py-4 px-4 font-semibold text-base text-center whitespace-pre-line focus:outline-none active:opacity-80 transition hover:bg-primaryColor hover:text-white"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        );
      }

      case 6:
        return (
          <div className="flex flex-col flex-1 items-center text-center justify-center">
            <h1 className="text-primaryColor font-bold font-montserrat text-4xl leading-tight mb-6">
              Create your
              <br />
              free QUSHi account
            </h1>
            <div className="text-primaryColor text-center mb-2">
              <div className="text-2xl font-montserrat">Start with</div>
              <div className="text-5xl font-bold font-montserrat leading-tight my-1">
                $1,000,000
              </div>
              <div className="text-xl font-montserrat">in virtual money</div>
            </div>
            {/* Client: "Put 'or credit card required.' on line two, both lines
                center aligned."

                A hard break rather than leaving it to the width. At this size
                the sentence wrapped on its own after "credit card" and dropped
                a single word, "required.", onto line two. The break moves the
                split to the natural phrase boundary; text-center on the
                paragraph is what centres both lines. */}
            <p className="text-greenXD text-center font-semibold mt-4 mb-2 font-montserrat">
              No brokerage account
              <br />
              or credit card required.
            </p>
            {/* One checkbox, above all three signup routes, exactly as asked -
                Apple, Google and email are each blocked until it is ticked. */}
            <div className="w-full max-w-sm mx-auto mt-4">
              <LegalAgreementCheckbox
                checked={legalAccepted}
                onChange={handleLegalChange}
                error={legalError}
              />
            </div>
            <div className="onboarding-social mt-2">
              <SocialLogin
                gate={{ accepted: legalAccepted, onBlocked: blockUntilAccepted }}
              />
            </div>
            <div className="flex justify-center mt-6">
              <button
                type="button"
                onClick={goToEmailSignup}
                className="text-primaryColor font-bold underline font-montserrat focus:outline-none"
              >
                Sign up with email
              </button>
            </div>
            <div className="flex justify-center mt-6">
              <Link
                to="/auth/login"
                className="text-primaryColor font-bold underline font-montserrat"
              >
                Already have an account? Sign in
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const showSkip = step >= 1 && step <= 4;
  const skipButton = showSkip ? (
    <button
      type="button"
      onClick={() => goToChooser()}
      className="absolute top-6 right-6 text-primaryColor text-base font-semibold font-montserrat focus:outline-none active:opacity-80"
    >
      Skip
    </button>
  ) : null;

  /* ─── Desktop layout ─── */
  if (!isMobile) {
    return (
      <div className="flex h-screen">
        {/* Left: static QUSHi branding panel */}
        <div className="w-1/2 bg-primaryColor flex flex-col items-center justify-center">
          {/* Brand lockup — see auth2/Login/index.jsx for the sizing rationale.
              Tagline sits slightly narrower than the wordmark; keep in sync. */}
          <img src="/Logo.png" alt="QUSHi" className="w-64" />
          <p
            className="text-white text-sm mt-4 uppercase whitespace-nowrap"
            style={{ letterSpacing: "0.3em", marginRight: "-0.3em" }}
          >
            TRADE TRACK PROVE IT
          </p>
        </div>

        {/* Right: onboarding content */}
        <div className="w-1/2 relative flex items-center justify-center px-16 py-12 min-h-screen" style={{ backgroundColor: "#f3f1ee" }}>
          {skipButton}
          <div className="w-full max-w-md flex flex-col" style={{ minHeight: "80vh" }}>
            <div className="flex-1 flex flex-col justify-center" style={{ pointerEvents: navLock ? "none" : "auto" }}>
              {renderStep()}
            </div>
            <div className="flex justify-center py-6">
              <QushiLogo />
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ─── Mobile layout ─── */
  return (
    <div className="min-h-screen w-full relative flex flex-col px-8 pb-8 text-center"
      style={{
        backgroundColor: "#F2F0EC",
        // The insets keep the copy clear of the status bar and home indicator if
        // the app ever hands the web view the full screen. The negative margins
        // that used to sit here were an attempt at the white-strip bug and never
        // reached it - the strips are the document canvas, which no inner element
        // can paint; useAuthScreenBackground() above is what actually fills them.
        // Worse, the margins would have pulled this screen's own content up under
        // the status bar the moment those insets stopped being zero.
        paddingTop: "calc(12vh + env(safe-area-inset-top, 0px))",
        paddingBottom: "calc(12vh + env(safe-area-inset-bottom, 0px))",
      }}
    >
      {skipButton}
      <div className="flex-1" style={{ pointerEvents: navLock ? "none" : "auto" }}>
        {renderStep()}
      </div>
      <div className="flex justify-center py-6">
        <QushiLogo />
      </div>
    </div>
  );
}

export default MobileOnboarding;
